import { Injectable } from '@angular/core';
import { BehaviorSubject, distinctUntilChanged, Observable } from 'rxjs';
import { Buffer } from 'buffer';

import mqtt, { MqttClient, IClientOptions } from 'mqtt';
/**
 * Thin wrapper around the `mqtt` client.
 *
 * Responsibilities:
 * - Create a WebSocket MQTT client for `ws://127.0.0.1:9001`.
 */
@Injectable({ providedIn: 'root' })
export class MqttService {
  /** URL of the local Mosquitto WebSocket endpoint. */
  private readonly brokerUrl = 'ws://127.0.0.1:9001'; // TODO - Make brokerUrl value configurable (port 9001 maybe in used)
  private mqttClient: MqttClient | null = null;
  private mqttConnectPromise: Promise<MqttClient> | null = null;
  private mqttOnConnectHandler: (() => void) | null = null;
  private mqttOnErrorHandler: ((error: Error) => void) | null = null;
  private mqttOnDisconnectBeforeConnectHandler: (() => void) | null = null;
  private readonly mqttConnected$ = new BehaviorSubject<boolean>(false);
  private readonly topicSubscriptions = new WeakMap<
    MqttClient,
    { counts: Map<string, number>; pending: Map<string, Promise<void>> }
  >();

  /**
   * Create a new MQTT client instance using a random clientId.
   */
  public createLocalClient(clientIdPrefix = 'client'): MqttClient {
    const clientId = `${clientIdPrefix}-${crypto.randomUUID()}`;

    const opts: IClientOptions = {
      clientId,
      reconnectPeriod: 0,
      clean: true,
    };

    return mqtt.connect(this.brokerUrl, opts);
  }

  public mqttConnect(clientIdPrefix = 'client'): Promise<MqttClient> {
    // Reuse the active singleton connection so one browser tab doesn't open multiple WS clients.
    if (this.mqttClient && this.mqttConnected$.value) return Promise.resolve(this.mqttClient);
    if (this.mqttConnectPromise) return this.mqttConnectPromise;
    const mqttClient = this.createLocalClient(clientIdPrefix);
    this.mqttClient = mqttClient;
    this.mqttConnectPromise = new Promise<MqttClient>((resolve, reject) =>
      this.registerMqttHandlers(mqttClient, resolve, reject)
    ).finally(() => {
      this.mqttConnectPromise = null;
    });
    return this.mqttConnectPromise;
  }

  public observeConnectionStatus(): Observable<boolean> {
    return this.mqttConnected$.asObservable().pipe(distinctUntilChanged());
  }

  private registerMqttHandlers(
    mqttClient: MqttClient,
    resolve: (value: MqttClient | PromiseLike<MqttClient>) => void,
    reject: (reason?: unknown) => void
  ): void {
    this.mqttOnConnectHandler = (): void => this.handleMqttConnected(mqttClient, resolve, reject);
    this.mqttOnErrorHandler = (error: Error): void => this.handleMqttError(mqttClient, reject, error);
    this.mqttOnDisconnectBeforeConnectHandler = (): void =>
      this.handleMqttError(mqttClient, reject, new Error('Local MQTT broker is unavailable'));

    mqttClient.on('connect', this.mqttOnConnectHandler);
    mqttClient.on('error', this.mqttOnErrorHandler);
    mqttClient.on('close', this.mqttOnDisconnectBeforeConnectHandler);
    mqttClient.on('offline', this.mqttOnDisconnectBeforeConnectHandler);
  }

  private handleMqttConnected(
    mqttClient: MqttClient,
    resolve: (value: MqttClient | PromiseLike<MqttClient>) => void,
    reject: (reason?: unknown) => void
  ): void {
    this.detachMqttHandlers(mqttClient);

    if (!this.mqttClient) {
      reject('insole-plan.cad.error.mqtt-client-create-failed');
      return;
    }

    this.mqttConnected$.next(true);
    this.attachMqttDisconnectHandlers(this.mqttClient);
    resolve(this.mqttClient);
  }

  private handleMqttError(mqttClient: MqttClient, reject: (reason?: unknown) => void, error: Error): void {
    this.detachMqttHandlers(mqttClient);
    if (this.mqttClient === mqttClient) this.mqttClient = null;
    this.mqttConnected$.next(false);
    this.clearTopicSubscriptions(mqttClient);
    this.endClient(mqttClient);
    reject(error);
  }

  private detachMqttHandlers(mqttClient: MqttClient): void {
    if (this.mqttOnConnectHandler) {
      mqttClient.off('connect', this.mqttOnConnectHandler);
      this.mqttOnConnectHandler = null;
    }
    if (this.mqttOnErrorHandler) {
      mqttClient.off('error', this.mqttOnErrorHandler);
      this.mqttOnErrorHandler = null;
    }
    if (this.mqttOnDisconnectBeforeConnectHandler) {
      mqttClient.off('close', this.mqttOnDisconnectBeforeConnectHandler);
      mqttClient.off('offline', this.mqttOnDisconnectBeforeConnectHandler);
      this.mqttOnDisconnectBeforeConnectHandler = null;
    }
  }

  private attachMqttDisconnectHandlers(mqttClient: MqttClient): void {
    const onDisconnected = (): void => this.handleMqttDisconnected(mqttClient);
    mqttClient.on('close', onDisconnected);
    mqttClient.on('offline', onDisconnected);
    mqttClient.on('reconnect', onDisconnected);
  }

  private handleMqttDisconnected(mqttClient: MqttClient): void {
    if (this.mqttClient === mqttClient) this.mqttClient = null;
    this.mqttConnected$.next(false);
    this.clearTopicSubscriptions(mqttClient);
  }

  /**
   * Safely terminate a client connection if it's still open.
   */
  public endClient(client: MqttClient | null): void {
    if (!client) return;
    try {
      client.end(true);
    } catch (e) {
      // ignore
    }
  }

  public mqttDisconnect(): void {
    const mqttClient = this.mqttClient;
    this.endClient(mqttClient);
    this.mqttClient = null;
    this.mqttConnected$.next(false);
    if (mqttClient) this.clearTopicSubscriptions(mqttClient);
  }

  public async subscribeToTopics(mqttClient: MqttClient, topics: string[], qos: 0 | 1 | 2 = 1): Promise<void> {
    const state = this.getTopicSubscriptionState(mqttClient);
    // Keep subscriptions sequential to fail fast on the first rejected topic.
    for (const topic of topics) {
      const currentCount = state.counts.get(topic) ?? 0;
      state.counts.set(topic, currentCount + 1);

      let subscription = state.pending.get(topic);
      if (!subscription) {
        subscription = new Promise<void>((resolve, reject) =>
          this.subscribeToTopicHandler(mqttClient, topic, qos, resolve, reject)
        );
        state.pending.set(topic, subscription);
      }

      try {
        await subscription;
        if (this.topicSubscriptions.get(mqttClient) !== state) throw new Error('MQTT client disconnected');
      } catch (error) {
        const count = state.counts.get(topic) ?? 0;
        if (count <= 1) state.counts.delete(topic);
        else state.counts.set(topic, count - 1);
        throw error;
      } finally {
        if (state.pending.get(topic) === subscription) {
          state.pending.delete(topic);
        }
      }
    }
  }

  private subscribeToTopicHandler(
    mqttClient: MqttClient,
    topic: string,
    qos: 0 | 1 | 2,
    resolve: () => void,
    reject: (reason?: unknown) => void
  ): void {
    mqttClient.subscribe(topic, { qos }, (error) => this.onSubscribeToTopic(resolve, reject, error));
  }

  private onSubscribeToTopic(resolve: () => void, reject: (reason?: unknown) => void, error: Error | null): void {
    if (error) {
      reject(error);
      return;
    }
    resolve();
  }

  public unsubscribeFromTopics(mqttClient: MqttClient, topics: string[]): void {
    const state = this.topicSubscriptions.get(mqttClient);
    if (!state) return;
    for (const topic of topics) {
      const currentCount = state.counts.get(topic) ?? 0;
      if (currentCount > 1) {
        state.counts.set(topic, currentCount - 1);
        continue;
      }
      if (currentCount === 1) {
        state.counts.delete(topic);
        mqttClient.unsubscribe(topic);
      }
    }
  }

  private getTopicSubscriptionState(mqttClient: MqttClient): {
    counts: Map<string, number>;
    pending: Map<string, Promise<void>>;
  } {
    let state = this.topicSubscriptions.get(mqttClient);
    if (!state) {
      state = { counts: new Map(), pending: new Map() };
      this.topicSubscriptions.set(mqttClient, state);
    }
    return state;
  }

  private clearTopicSubscriptions(mqttClient: MqttClient): void {
    this.topicSubscriptions.delete(mqttClient);
  }

  /** Publish JSON with QoS. */
  public publishJson(client: MqttClient, topic: string, payload: unknown, qos: 0 | 1 | 2 = 1): Promise<void> {
    const body = JSON.stringify(payload);
    return new Promise((resolve, reject) => {
      client.publish(topic, body, { qos }, (error) => (error ? reject(error) : resolve()));
    });
  }

  public publishBinary(client: MqttClient, topic: string, payload: ArrayBuffer, qos: 0 | 1 | 2 = 1): Promise<void> {
    const body = Buffer.from(new Uint8Array(payload));
    return new Promise((resolve, reject) => {
      client.publish(topic, body, { qos }, (error) => (error ? reject(error) : resolve()));
    });
  }

  public decodeJsonText<T>(payloadText: string): T | null {
    try {
      return JSON.parse(payloadText) as T;
    } catch {
      return null;
    }
  }

  public payloadToText(payload: string | Uint8Array | ArrayBuffer): string | null {
    if (typeof payload === 'string') return payload;
    const bytes = this.toUint8ArraySafe(payload);
    // Treat empty payloads as "no value" (common in heartbeat-style MQTT messages).
    if (bytes.byteLength === 0) return null;
    return new TextDecoder().decode(bytes);
  }

  public toUint8ArraySafe(payload: Uint8Array | ArrayBuffer): Uint8Array {
    if (!payload) return new Uint8Array(0);

    if (payload instanceof Uint8Array) return payload;

    if (ArrayBuffer.isView(payload)) {
      return new Uint8Array(payload.buffer, payload.byteOffset, payload.byteLength);
    }

    if (payload instanceof ArrayBuffer) return new Uint8Array(payload);

    return new Uint8Array(0);
  }
}
